import React, { useMemo, useState } from 'react';

const lineMeta = {
  pending: { label: 'Pendiente', badge: 'border-amber-400/30 bg-amber-400/12 text-amber-200', ring: 'border-white/10' },
  checked_ok: { label: 'Revisado OK', badge: 'border-emerald-400/30 bg-emerald-400/12 text-emerald-200', ring: 'border-emerald-400/15' },
  checked_diff: { label: 'Con diferencia', badge: 'border-rose-400/30 bg-rose-400/12 text-rose-200', ring: 'border-rose-400/15' },
  added_during_control: { label: 'Agregado', badge: 'border-violet-400/30 bg-violet-400/12 text-violet-200', ring: 'border-violet-400/15' },
};

const formatDifferenceLabel = (difference) => {
  if (difference == null || Number.isNaN(Number(difference))) return 'Sin conteo';
  if (Number(difference) === 0) return 'Coincide';
  if (Number(difference) > 0) return `+${difference}`;
  return `${difference}`;
};

const differenceColor = (difference) => {
  if (difference == null || Number.isNaN(Number(difference))) return 'text-slate-300';
  if (Number(difference) === 0) return 'text-emerald-300';
  return Number(difference) > 0 ? 'text-amber-300' : 'text-rose-300';
};

const MiniData = ({ label, value, valueClassName = 'text-white' }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.04] px-2.5 py-2">
    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</p>
    <p className={`mt-1 text-sm font-black ${valueClassName}`}>{value}</p>
  </div>
);

const StockOperatorLineCard = ({
  line,
  onStartControl,
  onShowHistory,
  onInlineChange,
  onAdvanceToNextLine,
  editable = false,
  Icon,
}) => {
  const [expanded, setExpanded] = useState(false);
  const meta = lineMeta[line.status] || lineMeta.pending;
  const differenceLabel = useMemo(() => formatDifferenceLabel(line.differenceValue), [line.differenceValue]);
  const expectedValue = Number(line.expectedQuantity || 0);

  const handlePhysicalChange = (value) => {
    if (!onInlineChange) return;
    const nextValue = value === '' ? '' : Math.max(0, Number(value || 0));
    const numericCount = nextValue === '' ? null : Number(nextValue);
    const difference = numericCount == null ? null : numericCount - expectedValue;
    const nextStatus = numericCount == null ? 'pending' : difference === 0 ? 'checked_ok' : 'checked_diff';
    onInlineChange(line.id, {
      countedQuantity: numericCount,
      countedLabel: numericCount == null ? '-' : `${numericCount}`,
      differenceValue: difference,
      readingLabel: numericCount == null ? 'Todavía sin conteo cargado' : difference === 0 ? 'Coincide con el sistema' : 'Hay diferencia para revisar',
      status: nextStatus,
    });
  };

  const handlePhysicalKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    if (line.differenceValue != null && Number(line.differenceValue) !== 0) {
      event.currentTarget.form?.elements?.namedItem?.(`observation-${line.id}`)?.focus?.();
      return;
    }
    onAdvanceToNextLine?.(line.id);
  };

  const handleObservationChange = (value) => {
    if (!onInlineChange) return;
    onInlineChange(line.id, { noteLabel: value });
  };

  const handleObservationKeyDown = (event) => {
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    onAdvanceToNextLine?.(line.id);
  };

  return (
    <div className={`overflow-hidden rounded-[24px] border bg-slate-950 text-white shadow-[0_14px_32px_rgba(2,6,23,0.22)] ${meta.ring}`}>
      <div className="bg-[radial-gradient(circle_at_top_left,_rgba(34,211,238,0.08),_transparent_35%),linear-gradient(180deg,_rgba(15,23,42,1),_rgba(2,6,23,1))] p-3.5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.16em] ${meta.badge}`}>
                {meta.label}
              </span>
              {line.addedDuringControl && (
                <span className="inline-flex rounded-full border border-violet-400/30 bg-violet-400/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-violet-200">
                  Añadido
                </span>
              )}
            </div>
            <h4 className="truncate text-base font-black text-white">{line.productName}</h4>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-slate-400">
              {line.codeLabel && <span className="truncate">{line.codeLabel}</span>}
              {line.categoryLabel && <span>• {line.categoryLabel}</span>}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04] text-slate-300"
          >
            <Icon path={expanded ? 'M19.5 8.25l-7.5 7.5-7.5-7.5' : 'M4.5 15.75l7.5-7.5 7.5 7.5'} className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-3 grid grid-cols-3 gap-2">
          <MiniData label="Sistema" value={line.expectedLabel} />
          <MiniData label="Físico" value={line.countedLabel} valueClassName="text-cyan-200" />
          <MiniData label="Dif." value={differenceLabel} valueClassName={differenceColor(line.differenceValue)} />
        </div>

        {editable ? (
          <div className="mt-3 grid grid-cols-[110px_minmax(0,1fr)] gap-2">
            <input
              type="number"
              min="0"
              value={line.countedQuantity == null ? '' : line.countedQuantity}
              onChange={(event) => handlePhysicalChange(event.target.value)}
              onKeyDown={handlePhysicalKeyDown}
              className="w-full rounded-2xl border border-cyan-400/20 bg-cyan-400/10 px-3 py-2.5 text-center text-base font-bold text-white outline-none focus:ring-2 focus:ring-cyan-500/40"
              placeholder="0"
            />
            <textarea
              name={`observation-${line.id}`}
              value={line.noteLabel === 'Sin observación' ? '' : line.noteLabel}
              onChange={(event) => handleObservationChange(event.target.value)}
              onKeyDown={handleObservationKeyDown}
              rows={2}
              disabled={line.differenceValue === 0 || line.differenceValue == null}
              className={`w-full resize-none rounded-2xl border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-cyan-500/40 ${line.differenceValue === 0 || line.differenceValue == null ? 'cursor-not-allowed border-white/5 bg-white/[0.02] text-slate-500 placeholder:text-slate-500' : 'border-white/10 bg-white/[0.04] text-slate-200'}`}
              placeholder={line.differenceValue === 0 || line.differenceValue == null ? 'Se habilita si hay diferencia' : 'Motivo de la diferencia'}
            />
          </div>
        ) : null}

        <div className="mt-3 rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-2">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Lectura</p>
          <p className={`mt-1 text-sm font-semibold ${differenceColor(line.differenceValue)}`}>{line.readingLabel || differenceLabel}</p>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-white/10 bg-white/[0.03] px-3.5 py-3">
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 text-sm">
            <p className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Observación actual</p>
            <p className="mt-2 font-medium text-slate-200">{line.noteLabel}</p>
          </div>

          <div className="mt-3 flex flex-col gap-2 sm:flex-row">
            {!editable ? (
              <button
                onClick={() => line.canStartControl && onStartControl(line.productId, line.task)}
                disabled={!line.canStartControl}
                className={`flex-1 rounded-2xl px-4 py-3 font-semibold transition-colors ${line.canStartControl ? 'bg-cyan-300 text-slate-950 hover:bg-cyan-200' : 'cursor-not-allowed bg-slate-800 text-slate-500'}`}
              >
                {line.canStartControl ? (line.status === 'pending' ? 'Iniciar control' : 'Recontar producto') : 'Sin acceso para recontar'}
              </button>
            ) : null}
            <button
              onClick={() => onShowHistory(line.product)}
              className={`${editable ? 'flex-1' : ''} rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 font-semibold text-slate-200 transition-colors hover:bg-white/[0.08]`}
            >
              Historial de controles
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockOperatorLineCard;
